import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Diamond, LogOut, LayoutDashboard, Car, CalendarRange, Settings, Plus,
  Pencil, Trash2, Eye, EyeOff, Loader2, X, ArrowUp, ArrowDown, Ban, TrendingUp,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  CATEGORY_LABELS, PRICE_UNIT_LABELS, STATUS_LABELS, SPEC_FIELDS, SPEC_LABELS, formatPrice,
} from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const emptyListing = () => ({
  category: "cars", title: "", location: "", city: "", description: "", images: [],
  specs: {}, supplierPrice: 0, markupPercentage: null, priceUnit: "day", guests: 1,
  featured: false, published: true, unavailable: false,
});
const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-[#F5F7F5] [color-scheme:dark] outline-none focus:border-[#D4AF37] transition-colors";
const labelCls = "text-[10px] uppercase tracking-widest text-[#7E8C84] block mb-1.5";

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0E1512]/65 backdrop-blur-xl p-5">
      <div className="text-[10px] uppercase tracking-widest text-[#7E8C84] mb-2">{label}</div>
      <div className="font-display text-3xl text-[#F5F7F5]">{value}</div>
      {sub && <div className="text-xs text-[#D4AF37] mt-1 font-mono2">{sub}</div>}
    </div>
  );
}

function ListingEditor({ listing, onClose, onSaved }) {
  const isNew = !listing.id;
  const [form, setForm] = useState({ ...emptyListing(), ...listing, specs: { ...(listing.specs || {}) } });
  const [imagesText, setImagesText] = useState((listing.images || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const specFields = SPEC_FIELDS[form.category] || [];
  const _rawPreview = (Number(form.supplierPrice) || 0) * (1 + (form.markupPercentage === null || form.markupPercentage === "" ? 10 : Number(form.markupPercentage)) / 100);
  const customerPreview = Math.round(_rawPreview / 50) * 50;

  const setSpec = (k, v) => setForm({ ...form, specs: { ...form.specs, [k]: v } });

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      supplierPrice: Number(form.supplierPrice) || 0,
      markupPercentage: form.markupPercentage === "" || form.markupPercentage === null ? null : Number(form.markupPercentage),
      guests: Number(form.guests) || 1,
      images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await api.post("/admin/listings", payload);
      else await api.put(`/admin/listings/${listing.id}`, payload);
      toast.success(isNew ? "Annonce créée." : "Annonce mise à jour.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0E1512]/60 backdrop-blur-xl border border-[#D4AF37]/20 text-[#F5F7F5] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{isNew ? "Nouvelle annonce" : "Modifier l'annonce"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Catégorie</label>
              <select value={form.category} data-testid="editor-category" onChange={(e) => setForm({ ...form, category: e.target.value, specs: {} })} className={inputCls}>
                <option value="cars" className="bg-[#0E0D0A]">Voitures</option>
                <option value="yachts" className="bg-[#0E0D0A]">Yachts</option>
                <option value="properties" className="bg-[#0E0D0A]">Villas & Appartements</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Unité de prix</label>
              <select value={form.priceUnit} data-testid="editor-priceunit" onChange={(e) => setForm({ ...form, priceUnit: e.target.value })} className={inputCls}>
                {Object.entries(PRICE_UNIT_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#0E0D0A]">{v}</option>)}
              </select>
            </div>
          </div>

          <div><label className={labelCls}>Titre</label><input value={form.title} data-testid="editor-title" onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Localisation</label><input value={form.location} data-testid="editor-location" onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Ville (destination)</label><input value={form.city} data-testid="editor-city" onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Description</label><textarea rows={3} value={form.description} data-testid="editor-description" onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} /></div>

          {/* Pricing engine */}
          <div className="rounded-2xl border border-[#D4AF37]/20 bg-white/[0.04] p-4">
            <div className="text-[11px] tracking-luxe uppercase text-[#D4AF37] mb-3">Moteur de prix (privé)</div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={labelCls}>Prix fournisseur €</label><input type="number" value={form.supplierPrice} data-testid="editor-supplierprice" onChange={(e) => setForm({ ...form, supplierPrice: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Marge % (vide = globale)</label><input type="number" placeholder="10" value={form.markupPercentage ?? ""} data-testid="editor-markup" onChange={(e) => setForm({ ...form, markupPercentage: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Prix client (aperçu)</label><div className="w-full bg-[#0E1512]/60 backdrop-blur-xl border border-[#D4AF37]/30 rounded-2xl px-3 py-2.5 text-sm font-mono2 text-[#D4AF37]" data-testid="editor-customer-preview">{EUR(customerPreview)}</div></div>
            </div>
          </div>

          {/* Specs */}
          <div>
            <div className="text-[11px] tracking-luxe uppercase text-[#D4AF37] mb-3">Caractéristiques</div>
            <div className="grid grid-cols-2 gap-3">
              {specFields.map((f) => (
                <div key={f}><label className={labelCls}>{SPEC_LABELS[f]}</label><input value={form.specs[f] ?? ""} data-testid={`editor-spec-${f}`} onChange={(e) => setSpec(f, e.target.value)} className={inputCls} /></div>
              ))}
              <div><label className={labelCls}>Capacité (personnes)</label><input type="number" value={form.guests} data-testid="editor-guests" onChange={(e) => setForm({ ...form, guests: e.target.value })} className={inputCls} /></div>
            </div>
          </div>

          {/* Images */}
          <div>
            <label className={labelCls}>Photos (une URL par ligne — l'ordre = l'affichage)</label>
            <textarea rows={4} value={imagesText} data-testid="editor-images" onChange={(e) => setImagesText(e.target.value)} className={`${inputCls} resize-none font-mono2 text-xs`} />
            <div className="flex gap-2 mt-2 flex-wrap">
              {imagesText.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 8).map((u, i) => (
                <img key={i} src={u} alt="" className="w-14 h-14 object-cover rounded-md border border-white/10" />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-[#C7D0CA] cursor-pointer">
              <input type="checkbox" checked={form.published} data-testid="editor-published" onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" /> Publiée
            </label>
            <label className="flex items-center gap-2 text-sm text-[#C7D0CA] cursor-pointer">
              <input type="checkbox" checked={form.featured} data-testid="editor-featured" onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" /> Coup de cœur
            </label>
            <label className="flex items-center gap-2 text-sm text-[#C7D0CA] cursor-pointer">
              <input type="checkbox" checked={!!form.unavailable} data-testid="editor-unavailable" onChange={(e) => setForm({ ...form, unavailable: e.target.checked })} className="accent-[#FF3B30] w-4 h-4" /> Indisponible (temporaire)
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="editor-save" className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-white font-semibold py-3 text-sm tracking-widest uppercase transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
            </button>
            <button onClick={onClose} className="px-6 rounded-2xl border border-white/10 text-[#F5F7F5] text-sm tracking-widest uppercase hover:border-white/10 transition-colors">Annuler</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AvailabilityManager({ listing, onClose, onChanged }) {
  const [periods, setPeriods] = useState(listing.blockedPeriods || []);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");

  const add = async () => {
    if (!start || !end) { toast.error("Dates requises."); return; }
    try {
      const { data } = await api.post(`/admin/listings/${listing.id}/block`, { start, end, reason: reason || "Bloqué" });
      setPeriods(data.blockedPeriods);
      setStart(""); setEnd(""); setReason("");
      toast.success("Période bloquée.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (pid) => {
    try {
      const { data } = await api.delete(`/admin/listings/${listing.id}/block/${pid}`);
      setPeriods(data.blockedPeriods);
      toast.success("Période débloquée.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0E1512]/60 backdrop-blur-xl border border-[#D4AF37]/20 text-[#F5F7F5] max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Disponibilités — {listing.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Début</label><input type="date" value={start} data-testid="block-start" onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Fin</label><input type="date" value={end} data-testid="block-end" onChange={(e) => setEnd(e.target.value)} className={inputCls} /></div>
          </div>
          <input placeholder="Motif (optionnel)" value={reason} data-testid="block-reason" onChange={(e) => setReason(e.target.value)} className={inputCls} />
          <button onClick={add} data-testid="block-add" className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-white font-semibold py-2.5 text-sm tracking-widest uppercase transition-colors">
            <Ban className="w-4 h-4" /> Bloquer cette période
          </button>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {periods.length === 0 ? <p className="text-[#7E8C84] text-sm text-center py-4">Aucune période bloquée.</p> : periods.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-2xl bg-white/[0.04] border border-white/[0.08] px-4 py-3" data-testid={`block-item-${p.id}`}>
                <div className="text-sm"><span className="font-mono2 text-[#D4AF37]">{p.start} → {p.end}</span><span className="text-[#7E8C84] ml-2">{p.reason}</span></div>
                <button onClick={() => remove(p.id)} data-testid={`block-remove-${p.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [managingAvail, setManagingAvail] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, l, b, cfg] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/listings"),
        api.get("/admin/bookings"), api.get("/admin/settings"),
      ]);
      setStats(s.data); setListings(l.data); setBookings(b.data); setSettings(cfg.data);
    } catch (e) { toast.error("Erreur de chargement des données admin."); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const togglePublish = async (l) => {
    await api.post(`/admin/listings/${l.id}/publish`, { published: !l.published });
    loadAll();
  };
  const del = async (l) => {
    if (!window.confirm(`Supprimer "${l.title}" ?`)) return;
    await api.delete(`/admin/listings/${l.id}`);
    toast.success("Annonce supprimée.");
    loadAll();
  };
  const updateBooking = async (id, status) => {
    await api.put(`/admin/bookings/${id}`, { status });
    toast.success("Statut mis à jour.");
    loadAll();
  };
  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Réglages enregistrés.");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-transparent text-[#F5F7F5]">
      <header className="border-b border-white/[0.08] bg-white/[0.04] sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Diamond className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-display text-xl">GROZNY <span className="text-[#D4AF37]">Admin</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#7E8C84] hidden sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-[#C7D0CA] hover:text-[#D4AF37] transition-colors">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="bg-[#0E1512]/65 backdrop-blur-xl border border-white/[0.08] mb-8 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white gap-2"><LayoutDashboard className="w-4 h-4" /> Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="listings" data-testid="tab-listings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white gap-2"><Car className="w-4 h-4" /> Annonces</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white gap-2"><CalendarRange className="w-4 h-4" /> Demandes</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white gap-2"><Settings className="w-4 h-4" /> Réglages</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Annonces actives" value={stats.active_listings} sub={`${stats.total_listings} au total`} />
                <Stat label="Demandes en attente" value={stats.pending_bookings} sub={`${stats.total_bookings} au total`} />
                <Stat label="Revenu confirmé" value={EUR(stats.confirmed_revenue)} />
                <Stat label="Marge potentielle" value={EUR(stats.potential_margin)} sub={`Marge globale ${stats.globalMarkup}%`} />
                <Stat label="Voitures" value={stats.by_category.cars} />
                <Stat label="Yachts" value={stats.by_category.yachts} />
                <Stat label="Villas & Appts" value={stats.by_category.properties} />
                <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#0E1512]/60 to-transparent p-5 flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-[#D4AF37]" />
                  <div className="text-sm text-[#C7D0CA]">Prix fournisseur jamais exposés au public.</div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Listings */}
          <TabsContent value="listings">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">Catalogue ({listings.length})</h2>
              <button onClick={() => setEditing(emptyListing())} data-testid="add-listing-btn" className="flex items-center gap-2 rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-white font-semibold px-4 py-2.5 text-sm tracking-widest uppercase transition-colors">
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>
            <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
              <table className="w-full text-sm" data-testid="listings-table">
                <thead className="bg-[#0E1512]/65 backdrop-blur-xl text-[#93A099] text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Annonce</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Catégorie</th>
                    <th className="text-right px-4 py-3">Fournisseur</th>
                    <th className="text-right px-4 py-3">Client</th>
                    <th className="text-right px-4 py-3 hidden md:table-cell">Marge</th>
                    <th className="text-center px-4 py-3">État</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((l) => (
                    <tr key={l.id} className="border-t border-white/[0.06] hover:bg-[#0E1512]/[0.02]" data-testid={`listing-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={l.images?.[0]} alt="" className="w-12 h-10 object-cover rounded-md" />
                          <div><div className="text-[#F5F7F5] font-medium">{l.title}</div><div className="text-[#7E8C84] text-xs">{l.location}</div></div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-[#93A099]">{CATEGORY_LABELS[l.category]}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-[#93A099]">{EUR(l.supplierPrice)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-[#D4AF37]">{EUR(l.customerPrice)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-emerald-400 hidden md:table-cell">+{EUR(l.profit)} <span className="text-[#5E6B64]">({l.effectiveMarkup}%)</span></td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <button onClick={() => togglePublish(l)} data-testid={`toggle-publish-${l.id}`} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${l.published ? "bg-emerald-950/60 text-emerald-300" : "bg-slate-800 text-[#93A099]"}`}>
                            {l.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            {l.published ? "Publiée" : "Masquée"}
                          </button>
                          {l.unavailable && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF6B60]" data-testid={`unavail-badge-${l.id}`}>
                              <Ban className="w-3 h-3" /> Indisponible
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setManagingAvail(l)} data-testid={`avail-${l.id}`} title="Disponibilités" className="p-2 text-[#93A099] hover:text-[#D4AF37]"><CalendarRange className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(l)} data-testid={`edit-${l.id}`} title="Modifier" className="p-2 text-[#93A099] hover:text-[#D4AF37]"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => del(l)} data-testid={`delete-${l.id}`} title="Supprimer" className="p-2 text-[#93A099] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings">
            <h2 className="font-display text-2xl mb-6">Demandes de réservation ({bookings.length})</h2>
            <div className="space-y-3" data-testid="bookings-list">
              {bookings.length === 0 ? <p className="text-[#7E8C84] text-center py-12">Aucune demande pour le moment.</p> : bookings.map((b) => (
                <div key={b.id} className="rounded-2xl border border-white/[0.08] bg-[#0E1512]/65 backdrop-blur-xl p-5" data-testid={`booking-${b.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${b.type === "reservation" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-sky-950 text-sky-300"}`}>{b.type === "reservation" ? "Réservation" : "Devis"}</span>
                        <span className="font-display text-lg text-[#F5F7F5]">{b.listing_title}</span>
                      </div>
                      <div className="text-sm text-[#93A099]">{b.customer_name} · {b.email} · {b.phone || "—"}</div>
                      <div className="text-xs text-[#7E8C84] font-mono2 mt-1">
                        {b.start_date && b.end_date ? `${b.start_date} → ${b.end_date} · ` : ""}{b.guests} pers. · {EUR(b.customerPrice_snapshot)} {PRICE_UNIT_LABELS[b.priceUnit]}
                      </div>
                      {b.message && <div className="text-sm text-[#C7D0CA] mt-2 italic">"{b.message}"</div>}
                    </div>
                    <select value={b.status} data-testid={`booking-status-${b.id}`} onChange={(e) => updateBooking(b.id, e.target.value)} className="bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2 text-sm text-[#F5F7F5] outline-none focus:border-[#D4AF37]">
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#0E0D0A]">{v}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            {settings && (
              <div className="max-w-xl">
                <h2 className="font-display text-2xl mb-6">Réglages globaux</h2>
                <div className="rounded-2xl border border-white/[0.08] bg-[#0E1512]/65 backdrop-blur-xl p-6 space-y-4">
                  <div>
                    <label className={labelCls}>Marge globale par défaut (%)</label>
                    <input type="number" value={settings.globalMarkup} data-testid="settings-markup" onChange={(e) => setSettings({ ...settings, globalMarkup: Number(e.target.value) })} className={inputCls} />
                    <p className="text-xs text-[#7E8C84] mt-1.5">Appliquée à toutes les annonces sans marge personnalisée.</p>
                  </div>
                  <div><label className={labelCls}>Numéro WhatsApp (format international, ex: +33...)</label><input value={settings.whatsapp} data-testid="settings-whatsapp" onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Téléphone affiché</label><input value={settings.phone} data-testid="settings-phone" onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>Email de contact</label><input value={settings.email} data-testid="settings-email" onChange={(e) => setSettings({ ...settings, email: e.target.value })} className={inputCls} /></div>
                  <button onClick={saveSettings} data-testid="settings-save" className="w-full rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-white font-semibold py-3 text-sm tracking-widest uppercase transition-colors">Enregistrer les réglages</button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {editing && <ListingEditor listing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadAll(); }} />}
      {managingAvail && <AvailabilityManager listing={managingAvail} onClose={() => setManagingAvail(null)} onChanged={loadAll} />}
    </div>
  );
}
